/**
 * MediaBrowser
 *
 * One component, two contexts:
 *
 *   context="standalone" — Full Finder-like experience for the sidebar Media Center
 *     page. Shows search, sort, filter, all 4 view modes, checkbox multi-select,
 *     bulk delete/download, rename (via existing MediaItemActions), folder management.
 *
 *   context="picker" — Streamlined in-app selector used inside MediaPickerModal.
 *     Shows search, sort, filter, view modes. Selection highlight on click. No
 *     destructive actions, no folder management.
 *
 * Usage (standalone):
 *   const browser = useMediaBrowser({ mode: 'folder', viewModeStorageKey: 'media-center-view' })
 *   <MediaBrowser browser={browser} context="standalone" />
 *
 * Usage (picker):
 *   const browser = useMediaBrowser({ mode: 'folder' })
 *   <MediaBrowser browser={browser} context="picker" onSelect={handleSelect} selectedAssets={selectedAssets} />
 */

import { useCallback, useEffect } from 'react'
import {
    Search,
    Grid2X2,
    Grid3X3,
    List,
    Columns2,
    MoveLeft,
    FolderOpen,
    Download,
    Trash2,
    CheckSquare,
    Square,
    PlusSquare,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import LoadingSpinner from '@/components/ui/loading'
import { Button } from '@/components/ui/button'
import { toastHandler } from '@/components/utils/Toast'
import toast from 'react-hot-toast'
import noPicture from '@/assets/no-pictures.png'
import folderIcon from '@/assets/chat-folder-icon.png'
import ManageUploadMediaFiles from '@/modules/media/components/ManageUploadMediaFiles'
import ManageMediaFolders from '@/modules/media/components/ManageMediaFolders'
import { MediaItemActions } from '@/modules/media/components/MediaItemActions'
import { useDeleteDocuments, useBulkExport } from '@/services/folders/mutations'
import { keyDocuments } from '@/services/folders/queries'
import { toMediaAsset, isImageUrl, type MediaAsset } from '@/services/folders/mediaTypes'
import type { DocumentInterface } from '@/services/folders/types'
import type { UseMediaBrowserReturn, ViewMode, SortOption, FilterOption } from '@/hooks/useMediaBrowser'
import { useSocketContext } from '@/context'

// ── Props ─────────────────────────────────────────────────────────────────────

export interface MediaBrowserProps {
    browser: UseMediaBrowserReturn
    context: 'standalone' | 'picker'
    /** Picker-mode: allow uploads (non-destructive) inside picker. */
    allowUpload?: boolean
    /** Picker-mode: called on every item click. In single-select, caller closes modal. */
    onSelect?: (asset: MediaAsset) => void
    /** Picker-mode: ids of currently selected assets (for highlight). */
    selectedAssets?: MediaAsset[]
    multiSelect?: boolean
    /** Height class for the scrollable item area. Defaults to h-[480px]. */
    heightClass?: string
}

// ── Toolbar ───────────────────────────────────────────────────────────────────

const VIEW_MODES: { mode: ViewMode; icon: React.ReactNode; label: string }[] = [
    { mode: 'icon',        icon: <Grid3X3 size={15} />,  label: 'Icon' },
    { mode: 'comfortable', icon: <Grid2X2 size={15} />,  label: 'Comfortable' },
    { mode: 'list',        icon: <List size={15} />,     label: 'List' },
    { mode: 'columns',     icon: <Columns2 size={15} />, label: 'Columns' },
]

const SORT_LABELS: Record<SortOption, string> = {
    created_desc: 'Created (newest)',
    created_asc: 'Created (oldest)',
    updated_desc: 'Updated (newest)',
    updated_asc: 'Updated (oldest)',
    name_asc: 'Name A-Z',
}

function formatShortDate(value: any): string {
    if (!value) return '—'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString()
}

function Toolbar({
    browser,
    context,
    allowUpload,
}: {
    browser: UseMediaBrowserReturn
    context: 'standalone' | 'picker'
    allowUpload?: boolean
}) {
    const { searchInput, setSearchInput, sort, setSort, filter, setFilter, viewMode, setViewMode } = browser

    return (
        <div className='flex flex-col gap-2 mb-3'>
            {/* Row 1: search + view mode */}
            <div className='flex items-center gap-2'>
                <div className='relative flex-1'>
                    <Search size={14} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400' />
                    <input
                        type='text'
                        placeholder='Search media…'
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        className='w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400'
                    />
                </div>
                <div className='flex items-center border border-gray-200 rounded-lg p-0.5 gap-0.5'>
                    {VIEW_MODES.map(({ mode, icon, label }) => (
                        <button
                            key={mode}
                            type='button'
                            title={label}
                            onClick={() => setViewMode(mode)}
                            className={cn(
                                'p-1.5 rounded-md transition-colors',
                                viewMode === mode
                                    ? 'bg-violet-600 text-white'
                                    : 'text-gray-400 hover:text-gray-700',
                            )}
                        >
                            {icon}
                        </button>
                    ))}
                </div>
            </div>

            {/* Row 2: filter + sort */}
            <div className='flex items-center justify-between gap-2'>
                <div className='flex items-center gap-1'>
                    {(['all', 'ai_generated', 'uploaded'] as FilterOption[]).map(f => (
                        <button
                            key={f}
                            type='button'
                            onClick={() => setFilter(f)}
                            className={cn(
                                'px-2.5 py-1 text-xs rounded-full transition-colors border',
                                filter === f
                                    ? 'bg-violet-600 text-white border-transparent'
                                    : 'border-gray-200 text-gray-500 hover:border-gray-300',
                            )}
                        >
                            {f === 'all' ? 'All' : f === 'ai_generated' ? 'AI Generated' : 'Uploaded'}
                        </button>
                    ))}
                </div>
                <select
                    value={sort}
                    onChange={e => setSort(e.target.value as SortOption)}
                    className='text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 focus:outline-none focus:ring-1 focus:ring-violet-300'
                >
                    {Object.entries(SORT_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
            </div>

            {/* Upload + folder buttons */}
            {(context === 'standalone' || allowUpload) && (
                <div className='flex items-center gap-2'>
                    <ManageUploadMediaFiles
                        trigger={
                            <Button size='sm' className='flex items-center gap-1.5 h-8 text-xs'>
                                <PlusSquare size={13} /> Upload
                            </Button>
                        }
                        onSelectMedia={() => browser.refetch()}
                    />
                    {context === 'standalone' && (
                        <ManageMediaFolders
                            trigger={
                                <Button size='sm' variant='outline' className='flex items-center gap-1.5 h-8 text-xs'>
                                    <FolderOpen size={13} /> New Folder
                                </Button>
                            }
                            defaultTag='library'
                        />
                    )}
                </div>
            )}
        </div>
    )
}

// ── Bulk action bar (standalone only) ─────────────────────────────────────────

function BulkActionBar({
    selectedIds,
    onClearSelection,
    onSelectAll,
    onBulkDelete,
    onBulkDownload,
    isDeleting,
}: {
    selectedIds: Set<string>
    onClearSelection: () => void
    onSelectAll: () => void
    onBulkDelete: () => void
    onBulkDownload: () => void
    isDeleting: boolean
}) {
    const count = selectedIds.size
    if (!count) return null

    return (
        <div className='flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 mb-3'>
            <div className='flex items-center gap-2 text-sm font-medium text-violet-800'>
                <CheckSquare size={15} />
                {count} selected
            </div>
            <div className='flex items-center gap-2'>
                <button
                    type='button'
                    onClick={onSelectAll}
                    className='text-xs text-violet-600 hover:text-violet-800 underline'
                >
                    Select all
                </button>
                <button
                    type='button'
                    onClick={onBulkDownload}
                    className='flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-gray-200 hover:bg-gray-50'
                >
                    <Download size={12} /> Download
                </button>
                <button
                    type='button'
                    onClick={onBulkDelete}
                    disabled={isDeleting}
                    className='flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50'
                >
                    <Trash2 size={12} /> Delete
                </button>
                <button
                    type='button'
                    onClick={onClearSelection}
                    className='text-xs text-gray-400 hover:text-gray-600'
                >
                    ✕
                </button>
            </div>
        </div>
    )
}

// ── Individual item renderers ─────────────────────────────────────────────────

function ItemThumbnail({ item, size: _size = 96 }: { item: DocumentInterface; size?: number }) {
    const isVideo = item.url?.includes('.mp4') || item.url?.includes('.webm')
    const isPdf = item.url?.includes('.pdf')
    const isTxt = item.url?.includes('.txt')

    if (isPdf || isTxt) {
        return (
            <div className='w-full h-full flex items-center justify-center bg-gray-100 text-xs text-gray-400 font-medium'>
                {isPdf ? 'PDF' : 'TXT'}
            </div>
        )
    }
    if (isVideo) {
        return <video src={item.url} muted playsInline className='object-cover w-full h-full' />
    }
    return (
        <img
            src={(item as any).thumbnail_url || item.url}
            alt={item.name}
            loading='lazy'
            className='object-cover w-full h-full'
        />
    )
}

// ── Grid item (icon / comfortable views) ──────────────────────────────────────

function GridItem({
    item,
    context,
    viewMode,
    isChecked,
    isPickerSelected,
    onToggleSelect,
    onPickerSelect,
    onEnterFolder,
    showActions,
}: {
    item: DocumentInterface
    context: 'standalone' | 'picker'
    viewMode: ViewMode
    isChecked: boolean
    isPickerSelected: boolean
    onToggleSelect: () => void
    onPickerSelect: () => void
    onEnterFolder: () => void
    showActions: boolean
}) {
    const iconSize = viewMode === 'icon' ? 'w-16 h-16' : 'w-full aspect-square'

    if (item.isFolder) {
        return (
            <button
                type='button'
                onClick={onEnterFolder}
                className={cn(
                    'group flex flex-col items-center text-center gap-1 p-2 rounded-xl hover:bg-gray-50 transition-colors',
                    viewMode === 'icon' ? 'w-20' : 'w-full',
                )}
            >
                <img src={folderIcon} alt='folder' className={cn(iconSize, 'object-contain')} />
                <p className='text-[11px] text-gray-600 truncate w-full'>{item.name}</p>
            </button>
        )
    }

    const isImage = isImageUrl(item.url)

    const handleClick = () => {
        if (context === 'picker') {
            onPickerSelect()
        } else {
            // standalone: toggle checkbox selection
            onToggleSelect()
        }
    }

    return (
        <div
            className={cn(
                'group relative rounded-xl overflow-hidden border transition-all cursor-pointer',
                viewMode === 'icon' ? 'w-20' : 'w-full',
                isPickerSelected
                    ? 'border-violet-500 ring-2 ring-violet-400 ring-offset-1'
                    : isChecked
                    ? 'border-violet-300 ring-1 ring-violet-300'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm',
            )}
            onClick={handleClick}
        >
            <div className={cn('overflow-hidden bg-gray-50', viewMode === 'icon' ? 'w-16 h-16' : 'w-full aspect-square')}>
                {isImage ? (
                    <ItemThumbnail item={item} />
                ) : (
                    <ItemThumbnail item={item} />
                )}
                <div className='absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none' />
            </div>

            {/* Checkbox overlay — standalone only */}
            {context === 'standalone' && (
                <div
                    className={cn(
                        'absolute top-1.5 left-1.5 transition-opacity',
                        isChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                    )}
                    onClick={e => { e.stopPropagation(); onToggleSelect() }}
                >
                    {isChecked
                        ? <CheckSquare size={18} className='text-violet-600 bg-white rounded' />
                        : <Square size={18} className='text-gray-400 bg-white/80 rounded' />
                    }
                </div>
            )}

            {/* Picker selected badge */}
            {context === 'picker' && isPickerSelected && (
                <div className='absolute top-1.5 right-1.5 bg-violet-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold'>
                    ✓
                </div>
            )}

            {/* Standalone actions — hover only */}
            {context === 'standalone' && showActions && (
                <div
                    className='absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity'
                    onClick={e => e.stopPropagation()}
                >
                    <MediaItemActions item={item} setShowImageViewer={() => {}} />
                </div>
            )}

            {viewMode === 'comfortable' && (
                <div className='p-2'>
                    <p className='text-[11px] text-gray-700 truncate'>{item.name}</p>
                </div>
            )}
        </div>
    )
}

// ── List row ─────────────────────────────────────────────────────────────────

function ListRow({
    item,
    context,
    isChecked,
    isPickerSelected,
    onToggleSelect,
    onPickerSelect,
    onEnterFolder,
}: {
    item: DocumentInterface
    context: 'standalone' | 'picker'
    isChecked: boolean
    isPickerSelected: boolean
    onToggleSelect: () => void
    onPickerSelect: () => void
    onEnterFolder: () => void
}) {
    const handleClick = () => {
        if (item.isFolder) { onEnterFolder(); return }
        if (context === 'picker') onPickerSelect()
        else onToggleSelect()
    }

    return (
        <div
            className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                isPickerSelected
                    ? 'border-violet-400 bg-violet-50'
                    : isChecked
                    ? 'border-violet-300 bg-violet-50/50'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50',
            )}
            onClick={handleClick}
        >
            {/* Checkbox (standalone) */}
            {context === 'standalone' && !item.isFolder && (
                <div onClick={e => { e.stopPropagation(); onToggleSelect() }}>
                    {isChecked
                        ? <CheckSquare size={16} className='text-violet-600 shrink-0' />
                        : <Square size={16} className='text-gray-300 shrink-0' />
                    }
                </div>
            )}

            {/* Thumbnail */}
            <div className='w-10 h-10 rounded-md overflow-hidden bg-gray-100 shrink-0'>
                {item.isFolder
                    ? <img src={folderIcon} alt='folder' className='object-contain w-full h-full p-1' />
                    : <ItemThumbnail item={item} />
                }
            </div>

            <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-gray-800 truncate'>{item.name}</p>
                <p className='text-[11px] text-gray-400 flex items-center gap-2'>
                    <span className='uppercase'>{item.data_type}</span>
                    <span className='text-gray-300'>·</span>
                    <span>Created {formatShortDate((item as any).createdAt)}</span>
                    <span className='text-gray-300'>·</span>
                    <span>Updated {formatShortDate((item as any).updatedAt)}</span>
                </p>
            </div>

            {context === 'picker' && isPickerSelected && (
                <span className='text-xs bg-violet-600 text-white px-2 py-0.5 rounded-full shrink-0'>Selected</span>
            )}

            {context === 'standalone' && !item.isFolder && (
                <div onClick={e => e.stopPropagation()}>
                    <MediaItemActions item={item} setShowImageViewer={() => {}} />
                </div>
            )}
        </div>
    )
}

// ── Columns row ───────────────────────────────────────────────────────────────

function ColumnsRow({
    item,
    context,
    isChecked,
    isPickerSelected,
    onToggleSelect,
    onPickerSelect,
    onEnterFolder,
}: {
    item: DocumentInterface
    context: 'standalone' | 'picker'
    isChecked: boolean
    isPickerSelected: boolean
    onToggleSelect: () => void
    onPickerSelect: () => void
    onEnterFolder: () => void
}) {
    const handleClick = () => {
        if (item.isFolder) { onEnterFolder(); return }
        if (context === 'picker') onPickerSelect()
        else onToggleSelect()
    }

    return (
        <div
            className={cn(
                'grid grid-cols-[28px_32px_1fr_110px_110px_84px] items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs transition-all',
                isPickerSelected
                    ? 'border-violet-400 bg-violet-50'
                    : isChecked
                    ? 'border-violet-300 bg-violet-50/50'
                    : 'border-transparent hover:bg-gray-50',
            )}
            onClick={handleClick}
        >
            {context === 'standalone' && !item.isFolder && (
                <div onClick={e => { e.stopPropagation(); onToggleSelect() }}>
                    {isChecked
                        ? <CheckSquare size={14} className='text-violet-600 shrink-0' />
                        : <Square size={14} className='text-gray-300 shrink-0' />
                    }
                </div>
            )}
            <div className='w-8 h-8 rounded overflow-hidden bg-gray-100 shrink-0'>
                {item.isFolder
                    ? <img src={folderIcon} alt='folder' className='object-contain w-full h-full p-0.5' />
                    : <ItemThumbnail item={item} />
                }
            </div>
            <p className='truncate text-gray-700'>{item.name}</p>
            <span className='text-[11px] text-gray-500 tabular-nums hidden sm:block'>
                {formatShortDate((item as any).createdAt)}
            </span>
            <span className='text-[11px] text-gray-500 tabular-nums hidden sm:block'>
                {formatShortDate((item as any).updatedAt)}
            </span>
            {item.data_type && (
                <span className='text-[10px] text-gray-400 uppercase shrink-0 justify-self-end'>{item.data_type}</span>
            )}
        </div>
    )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function MediaBrowser({
    browser,
    context,
    allowUpload,
    onSelect,
    selectedAssets = [],
    multiSelect: _multiSelect = false,
    heightClass = 'h-[480px]',
}: MediaBrowserProps) {
    const {
        items,
        isLoading,
        viewMode,
        currentFolderId,
        folderHistory,
        folderName,
        enterFolder,
        goBack,
        selectedIds,
        toggleSelect,
        selectAll,
        clearSelection,
        isSelected,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
    } = browser

    const selectedAssetIds = new Set(selectedAssets.map(a => a._id))

    // Auto-refresh when an AI image generation completes
    const { socket } = useSocketContext()
    useEffect(() => {
        if (!socket) return
        const handler = () => refetch()
        socket.on('image-generated', handler)
        return () => { socket.off('image-generated', handler) }
    }, [socket, refetch])

    // ── Bulk delete mutation ──────────────────────────────────────────────────
    const { mutate: bulkDelete, isLoading: isDeleting } = useDeleteDocuments({
        refetch: [keyDocuments('')],
        onSuccess: (data: any) => {
            clearSelection()
            if (data?.blocked?.length) {
                const names = data.blocked.map((b: any) => b.managedBy).join(', ')
                toast.error(`${data.blocked.length} item(s) blocked — managed by ${names}. Delete them there.`)
            }
            toastHandler({ message: `Items deleted` })
        },
        onError: (error: any) => {
            const status = error?.response?.status
            const message = error?.response?.data?.error
            if (status === 409 && message) {
                toast.error(message)
            } else {
                toastHandler({ message: 'Delete failed' } as any)
            }
        },
    })

    // ── Bulk export mutation ──────────────────────────────────────────────────
    const { mutate: bulkExport } = useBulkExport({
        onSuccess: (blob: any) => {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'media-export.zip'
            a.click()
            URL.revokeObjectURL(url)
        },
        onError: () => toastHandler({ message: 'Download failed' } as any),
    })

    const handleBulkDelete = useCallback(() => {
        if (!selectedIds.size) return
        bulkDelete({ ids: Array.from(selectedIds) })
    }, [selectedIds, bulkDelete])

    const handleBulkDownload = useCallback(() => {
        if (!selectedIds.size) return
        bulkExport({ ids: Array.from(selectedIds), format: 'zip' })
    }, [selectedIds, bulkExport])

    // ── Grid layout by view mode ──────────────────────────────────────────────
    const gridClass =
        viewMode === 'icon'
            ? 'flex flex-wrap gap-3'
            : viewMode === 'comfortable'
            ? 'grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3'
            : viewMode === 'columns'
            ? 'grid grid-cols-1 sm:grid-cols-2 gap-1'
            : 'flex flex-col gap-1'

    const hasItems = items.some(i => i.isFolder || isImageUrl(i.url) || i.url)

    return (
        <div className='flex flex-col'>
            <Toolbar browser={browser} context={context} allowUpload={allowUpload} />

            {/* Folder breadcrumb */}
            {folderHistory.length > 0 && (
                <div className='flex items-center gap-2 mb-2'>
                    <button
                        type='button'
                        onClick={goBack}
                        className='flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors'
                    >
                        <MoveLeft size={15} />
                    </button>
                    {folderName && <p className='text-sm font-semibold text-gray-600'>{folderName}</p>}
                </div>
            )}

            {/* Bulk action bar */}
            {context === 'standalone' && (
                <BulkActionBar
                    selectedIds={selectedIds}
                    onClearSelection={clearSelection}
                    onSelectAll={selectAll}
                    onBulkDelete={handleBulkDelete}
                    onBulkDownload={handleBulkDownload}
                    isDeleting={isDeleting}
                />
            )}

            {/* Item grid */}
            <div className={cn('overflow-y-auto py-1', heightClass)}>
                {isLoading ? (
                    <div className='flex items-center justify-center h-full'>
                        <LoadingSpinner />
                    </div>
                ) : !hasItems ? (
                    <div className='flex flex-col items-center justify-center h-full gap-2'>
                        <img src={noPicture} alt='No media' className='h-[80px] opacity-40' />
                        <p className='text-gray-400 text-sm'>
                            {currentFolderId ? 'This folder is empty' : 'No media found'}
                        </p>
                    </div>
                ) : (
                    <div className='space-y-3'>
                        {viewMode === 'columns' && (
                            <div className='grid grid-cols-[28px_32px_1fr_110px_110px_84px] items-center gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400'>
                                <div className='hidden sm:block' />
                                <div />
                                <div>Name</div>
                                <div className='hidden sm:block'>Created</div>
                                <div className='hidden sm:block'>Updated</div>
                                <div className='text-right'>Type</div>
                            </div>
                        )}
                        <div className={gridClass}>
                            {items.map(item => {
                            const checked = context === 'standalone' && isSelected(item._id)
                            const pickerSelected = context === 'picker' && selectedAssetIds.has(item._id)

                            const handlePickerSelect = () => {
                                if (!onSelect) return
                                onSelect(toMediaAsset(item))
                            }

                            if (viewMode === 'list') {
                                return (
                                    <ListRow
                                        key={item._id}
                                        item={item}
                                        context={context}
                                        isChecked={checked}
                                        isPickerSelected={pickerSelected}
                                        onToggleSelect={() => toggleSelect(item._id)}
                                        onPickerSelect={handlePickerSelect}
                                        onEnterFolder={() => enterFolder(item._id)}
                                    />
                                )
                            }

                            if (viewMode === 'columns') {
                                return (
                                    <ColumnsRow
                                        key={item._id}
                                        item={item}
                                        context={context}
                                        isChecked={checked}
                                        isPickerSelected={pickerSelected}
                                        onToggleSelect={() => toggleSelect(item._id)}
                                        onPickerSelect={handlePickerSelect}
                                        onEnterFolder={() => enterFolder(item._id)}
                                    />
                                )
                            }

                            return (
                                <GridItem
                                    key={item._id}
                                    item={item}
                                    context={context}
                                    viewMode={viewMode}
                                    isChecked={checked}
                                    isPickerSelected={pickerSelected}
                                    onToggleSelect={() => toggleSelect(item._id)}
                                    onPickerSelect={handlePickerSelect}
                                    onEnterFolder={() => enterFolder(item._id)}
                                    showActions={context === 'standalone'}
                                />
                            )
                            })}
                        </div>

                        {hasNextPage && (
                            <div className='flex justify-center py-2'>
                                <Button
                                    variant='outline'
                                    onClick={fetchNextPage}
                                    disabled={isFetchingNextPage}
                                    className='text-xs'
                                >
                                    {isFetchingNextPage ? 'Loading…' : 'Load more'}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default MediaBrowser
